/**
 * Парсинг и сборка публичных URL для Supabase Storage.
 *
 * Контекст: до 2026-05-20 код хранил в БД полный публичный URL
 * (`https://hgjkuenjgqdxjnkqddve.supabase.co/storage/v1/object/public/<bucket>/<path>`).
 * При переезде на self-hosted (`api.stryzh.ru`) хост сменился, и старые
 * абсолютные URL стали битыми. С 2026-05-20 пишем в БД относительный
 * `bucket/path` (без хоста и без `/storage/v1/object/public/`), а полный URL
 * собираем на чтении через клиент.
 *
 * Эта утилита поддерживает оба формата (legacy + новый), чтобы переходный
 * период не требовал одномоментной миграции всех писателей и данных.
 * Логика дублируется в мобильном репо `stryzh/lib/core/utils/storage_url.dart`.
 */

import type { SupabaseClient } from '@supabase/supabase-js'
import { createClient } from '@/api/supabase'
import { USE_MOCK } from '@/config'

export const KNOWN_STORAGE_BUCKETS = new Set<string>([
  'inspection-photos',
  'user-feedback',
])

export interface StoredStorageRef {
  /** Если в БД лежал готовый абсолютный URL (legacy) — он тут. */
  absoluteUrl: string | null
  /** Имя bucket'а, если значение распознано как относительная ссылка. */
  bucket: string | null
  /** Path внутри bucket'а. */
  path: string | null
}

const EMPTY_REF: StoredStorageRef = {
  absoluteUrl: null,
  bucket: null,
  path: null,
}

/**
 * Чистый парсер сохранённого storage-значения (без зависимостей от Supabase).
 * Форматы:
 *  1. null/'' → пустая ссылка.
 *  2. `http(s)://...` → absoluteUrl, bucket/path = null (legacy).
 *  3. `bucket/path` где `bucket ∈ KNOWN_STORAGE_BUCKETS` → bucket+path.
 *  4. голый `path` → defaultBucket + path.
 */
export function parseStoredStorageRef(
  stored: string | null | undefined,
  defaultBucket: string = 'inspection-photos',
): StoredStorageRef {
  if (stored == null) return EMPTY_REF
  const v = stored.trim()
  if (!v) return EMPTY_REF
  if (/^https?:\/\//i.test(v)) {
    return { absoluteUrl: v, bucket: null, path: null }
  }
  const slash = v.indexOf('/')
  if (slash > 0) {
    const maybeBucket = v.substring(0, slash)
    const maybePath = v.substring(slash + 1)
    if (maybePath && KNOWN_STORAGE_BUCKETS.has(maybeBucket)) {
      return { absoluteUrl: null, bucket: maybeBucket, path: maybePath }
    }
  }
  return { absoluteUrl: null, bucket: defaultBucket, path: v }
}

/**
 * Преобразует сохранённое значение storage-ссылки в публичный URL.
 * Для legacy-абсолютных URL — возвращается как есть; для нового формата —
 * собирается через `supabase.storage.from(bucket).getPublicUrl(path)`.
 */
export function resolvePublicStorageUrl(
  supabase: SupabaseClient,
  stored: string | null | undefined,
  defaultBucket: string = 'inspection-photos',
): string | null {
  const ref = parseStoredStorageRef(stored, defaultBucket)
  if (ref.absoluteUrl) return ref.absoluteUrl
  if (!ref.bucket || !ref.path) return null
  const { data } = supabase.storage.from(ref.bucket).getPublicUrl(ref.path)
  return data.publicUrl || null
}

/**
 * Удобная обёртка над [resolvePublicStorageUrl] для UI-кода: сама берёт
 * singleton supabase-клиент через `createClient()`. В mock-режиме
 * (`USE_MOCK = true`) возвращает значение без преобразования —
 * фикстуры обычно уже содержат готовые URL.
 */
export function resolveStoredPublicUrl(
  stored: string | null | undefined,
  defaultBucket: string = 'inspection-photos',
): string | null {
  if (USE_MOCK) return stored?.trim() || null
  return resolvePublicStorageUrl(createClient(), stored, defaultBucket)
}
